import React, { useState } from 'react';
import { Typography, List, Tag, Space, Card, Row, Col, Flex, Switch, Form, Input, Button, theme, Tooltip } from 'antd';
import ResponsiveModal from '../../../Shared/ResponsiveModal/ResponsiveModal';
import { HighlightOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { beautifyText } from '../../../Messages/MessagesServices';
import { useTranslation } from 'react-i18next';
import { ICON_SIZE, commonIconButtonStyle } from '../../../Shared/Styles/iconButton';
import { formatShortDate } from '../../../../Utils/date';

const { Text } = Typography;

const EntryItem = ({ item, onDelete, onToggle, onUpdate, updating }) => {
    const { t, i18n } = useTranslation();
    const { token } = theme.useToken();
    const [isHovered, setIsHovered] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [form] = Form.useForm();

    const cardStyle = {
        marginBottom: 8,
        boxShadow: isHovered ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 1px 4px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s',
        borderRadius: 4,
        width: '100%'
    };

    const contentBoxStyle = {
        padding: '0',
        borderRadius: 4,
    };

    const questionBoxStyle = {
        ...contentBoxStyle,
        backgroundColor: token.colorInfoBg,
        border: `1px solid ${token.colorBorderSecondary}`
    };

    const answerBoxStyle = {
        ...contentBoxStyle,
        backgroundColor: token.colorFillSecondary,
        border: `1px solid ${token.colorBorder}`
    };

    const contentOpacityStyle = item.is_disabled ? { opacity: 0.6 } : {};

    const actions = isHovered && !isEditing ? [
        <Space>
            <EditOutlined
                key="edit"
                style={{ fontSize: 16, color: '#1890ff' }}
                onClick={() => {
                    setIsEditing(true);
                    // initialize form with current item values
                    if (item.entry_type === 'question_answer') {
                        form.setFieldsValue({ question: item.data.question, answer: item.data.answer });
                    } else {
                        form.setFieldsValue({ content: item.data.content });
                    }
                }}
            />
            <DeleteOutlined
                key="delete"
                style={{ fontSize: 16, color: '#ff4d4f' }}
                onClick={() => setDeleteModalVisible(true)}
            />
        </Space>
    ] : [];

    const handleCancel = () => {
        form.resetFields();
        setIsEditing(false);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (onUpdate) {
                await onUpdate(item, values);
            }
            setIsEditing(false);
        } catch (e) {
            // validation errors are handled by antd
        }
    };

    const confirmDelete = async () => {
        try {
            await onDelete(item.id);
        } finally {
            setDeleteModalVisible(false);
        }
    };

    return (
        <List.Item style={{ padding: 0, width: '100%' }}>
            <Card
                style={cardStyle}
                styles={{ body: { padding: '12px' } }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <Row>
                    <Col span={24}>
                        <Flex style={{ width: '100%' }} align='center' gap={8}>
                            <Space style={{ flexGrow: 1 }}>
                                <Tag color={item.entry_type === 'question_answer' ? 'blue' : 'green'}
                                    style={{ padding: '2px 8px', fontSize: 12, margin: 0 }}>
                                    {item.entry_type === 'question_answer' ? t('qa') : t('business_info')}
                                </Tag>
                            </Space>
                            <Switch
                                checked={!item.is_disabled}
                                onChange={(checked) => onToggle && onToggle(item, !checked)}
                                size="small"
                            />
                            {actions}
                            <Text type="secondary" style={{ fontSize: 12, padding: 0 }}>
                                {formatShortDate(item.created_at, i18n.language)}
                            </Text>
                        </Flex>

                        <div style={contentOpacityStyle}>
                            {isEditing ? (
                                <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
                                    {item.entry_type === 'question_answer' ? (
                                        <Space direction="vertical" style={{ width: '100%' }} size={0}>
                                            <Form.Item
                                                name="question"
                                                label={t('question')}
                                                rules={[{ required: true, message: t('please_enter_question') }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Input.TextArea autoSize={{ minRows: 2 }} placeholder={t('enter_question_here')} />
                                            </Form.Item>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 0 }}>
                                                <Tooltip title={t('beautify_tooltip_apply')} mouseEnterDelay={0.25}>
                                                    <Button
                                                        type="text"
                                                        shape="circle"
                                                        icon={<HighlightOutlined style={{ fontSize: ICON_SIZE }} />}
                                                        size="small"
                                                        style={{ ...commonIconButtonStyle, color: '#7F56D9' }}
                                                        onClick={async () => {
                                                            const current = form.getFieldValue('question');
                                                            if (!current || !current.trim()) return;
                                                            try {
                                                                const result = await beautifyText(current);
                                                                const beautified = result?.data?.text || result?.text || '';
                                                                if (beautified) form.setFieldsValue({ question: beautified });
                                                            } catch (e) { }
                                                        }}
                                                    />
                                                </Tooltip>
                                            </div>

                                            <Form.Item
                                                name="answer"
                                                style={{ marginBottom: 0 }}
                                                label={t('answer')}
                                                rules={[{ required: true, message: t('please_enter_answer') }]}
                                            >
                                                <Input.TextArea autoSize={{ minRows: 3 }} placeholder={t('enter_answer_here')} />
                                            </Form.Item>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <Tooltip title={t('beautify_tooltip_apply')} mouseEnterDelay={0.25}>
                                                    <Button
                                                        type="text"
                                                        shape="circle"
                                                        icon={<HighlightOutlined style={{ fontSize: ICON_SIZE }} />}
                                                        size="small"
                                                        style={{ ...commonIconButtonStyle, color: '#7F56D9' }}
                                                        onClick={async () => {
                                                            const current = form.getFieldValue('answer');
                                                            if (!current || !current.trim()) return;
                                                            try {
                                                                const result = await beautifyText(current);
                                                                const beautified = result?.data?.text || result?.text || '';
                                                                if (beautified) form.setFieldsValue({ answer: beautified });
                                                            } catch (e) { }
                                                        }}
                                                    />
                                                </Tooltip>
                                            </div>
                                        </Space>
                                    ) : (
                                        <>
                                            <Form.Item
                                                name="content"
                                                style={{ marginBottom: 0 }}
                                                label={t('business_information')}
                                                rules={[{ required: true, message: t('please_enter_business_information') }]}
                                            >
                                                <Input.TextArea autoSize={{ minRows: 4 }} placeholder={t('enter_business_information_here')} />
                                            </Form.Item>
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 0 }}>
                                                <Tooltip title={t('beautify_tooltip_apply')} mouseEnterDelay={0.25}>
                                                    <Button
                                                        type="text"
                                                        shape="circle"
                                                        icon={<HighlightOutlined style={{ fontSize: ICON_SIZE }} />}
                                                        size="small"
                                                        style={{ ...commonIconButtonStyle, color: '#7F56D9' }}
                                                        onClick={async () => {
                                                            const current = form.getFieldValue('content');
                                                            if (!current || !current.trim()) return;
                                                            try {
                                                                const result = await beautifyText(current);
                                                                const beautified = result?.data?.text || result?.text || '';
                                                                if (beautified) form.setFieldsValue({ content: beautified });
                                                            } catch (e) { }
                                                        }}
                                                    />
                                                </Tooltip>
                                            </div>
                                        </>
                                    )}
                                    <Flex gap={8} wrap style={{ marginTop: 8 }}>
                                        <Button onClick={handleCancel} block>
                                            {t('cancel')}
                                        </Button>
                                        <Button type="primary" onClick={handleSave} loading={updating} block>
                                            {t('update_entry')}
                                        </Button>
                                    </Flex>
                                </Form>
                            ) : (
                                <>
                                    {item.entry_type === 'question_answer' ? (
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Card style={questionBoxStyle} styles={{ body: { padding: '12px' } }} bordered={false}>
                                                <Text strong style={{ fontSize: 13, color: token.colorLink, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {t('q_prefix')} {item.data.question}
                                                </Text>
                                            </Card>
                                            <Card style={answerBoxStyle} styles={{ body: { padding: '12px' } }} bordered={false}>
                                                <Text style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {t('a_prefix')} {item.data.answer}
                                                </Text>
                                            </Card>
                                        </Space>
                                    ) : (
                                        <Space direction="vertical" style={{ width: '100%' }}>
                                            <Card styles={{ body: { padding: '12px' } }} style={answerBoxStyle} bordered={false}>
                                                <Text style={{ fontSize: 13, padding: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {item.data.content}
                                                </Text>
                                            </Card>
                                        </Space>
                                    )}
                                </>
                            )}
                        </div>
                    </Col>
                </Row>
            </Card>
            <ResponsiveModal
                title={t('delete_this_entry')}
                open={deleteModalVisible}
                onOk={confirmDelete}
                onCancel={() => setDeleteModalVisible(false)}
                okText={t('yes')}
                cancelText={t('no')}
                okButtonProps={{ danger: true }}
                desktopWidth={420}
            >
                <div>
                    {item.entry_type === 'question_answer' ? (
                        <>
                            <div style={{ marginTop: 8, opacity: 0.8 }}>
                                <Text type="secondary">{t('q_prefix')} {item.data?.question}</Text>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ marginTop: 8, opacity: 0.8 }}>
                                <Text type="secondary">{(item.data?.content || '').slice(0, 180)}{(item.data?.content || '').length > 80 ? '…' : ''}</Text>
                            </div>
                        </>
                    )}
                </div>
            </ResponsiveModal>
        </List.Item >
    );
};

export default EntryItem;
